// Top header strip for the campaign live view.
//
// Shows the editable campaign name, a status badge, run counters, cumulative
// FlexCredit cost vs budget, and the control buttons (pause/resume/stop/fork).
//
// The header does NOT own the fork dialog itself — it imports and mounts W5's
// <ForkModal /> from ../provenance.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Flex,
  Text,
  TextField,
  Tooltip,
} from '@radix-ui/themes';

import type { CampaignState, ControlAction } from '../types';
import { useControlMutation } from '../api';
import { ForkModal } from '../provenance';

// ---------------------------------------------------------------------------
// Status badge colour mapping
// ---------------------------------------------------------------------------

type RadixBadgeColor = 'gray' | 'green' | 'amber' | 'blue' | 'red';

const STATUS_COLOR: Record<CampaignState['status'], RadixBadgeColor> = {
  running: 'green',
  paused: 'amber',
  done: 'blue',
  failed: 'red',
  configuring: 'gray',
};

const STATUS_LABEL: Record<CampaignState['status'], string> = {
  running: 'Running',
  paused: 'Paused',
  done: 'Done',
  failed: 'Failed',
  configuring: 'Configuring',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type CampaignHeaderProps = {
  state: CampaignState;
};

export function CampaignHeader({ state }: CampaignHeaderProps) {
  const controlMutation = useControlMutation(state.id);

  // ---- inline rename -----------------------------------------------------
  const [nameDraft, setNameDraft] = useState(state.config.name);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep local draft in sync when the upstream state.config.name changes.
  useEffect(() => {
    if (!editing) setNameDraft(state.config.name);
  }, [state.config.name, editing]);

  const commitRename = () => {
    setEditing(false);
    if (nameDraft === state.config.name) return;
    // TODO: persist name edit — consult LEAD. The frozen API contract has
    // no PATCH /api/campaigns/:id/config endpoint, so for now rename is a
    // local-only draft that resets on refresh.
    // eslint-disable-next-line no-console
    console.info(
      '[CampaignHeader] rename kept local-only (no PATCH endpoint in contract):',
      nameDraft,
    );
  };

  // ---- counters + cost ---------------------------------------------------
  const totalPoints = state.points.length;
  const done = state.pointsComplete;
  const inFlight = state.pointsInFlight;
  const autonomous = state.metrics.autonomousRunCount;
  const manual = state.metrics.manualRunCount;

  const maxCostFc = useMemo(
    () => state.config.budget * state.config.flexCreditsPerHighFiRun,
    [state.config.budget, state.config.flexCreditsPerHighFiRun],
  );
  const cumulativeFc = state.metrics.cumulativeCostFlexCredits ?? 0;

  // ---- controls ----------------------------------------------------------
  const canPause = state.status === 'running';
  const canResume = state.status === 'paused' || state.status === 'configuring';
  const canStop =
    state.status === 'running' ||
    state.status === 'paused' ||
    state.status === 'configuring';

  const sendControl = (action: ControlAction) => {
    controlMutation.mutate(action);
  };

  const [confirmStop, setConfirmStop] = useState(false);
  const [forkOpen, setForkOpen] = useState(false);

  return (
    <Box
      style={{
        borderBottom: '1px solid var(--gray-a5)',
        background: 'var(--color-panel-solid)',
        padding: '12px 16px',
      }}
    >
      <Flex align="center" justify="between" gap="4" wrap="wrap">
        {/* Left: name + status */}
        <Flex align="center" gap="3" style={{ minWidth: 0, flex: 1 }}>
          {editing ? (
            <TextField.Root
              ref={inputRef}
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.currentTarget.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') {
                  setNameDraft(state.config.name);
                  setEditing(false);
                }
              }}
              style={{ minWidth: 280 }}
            />
          ) : (
            <Tooltip content="Click to rename">
              <Text
                size="4"
                weight="bold"
                onClick={() => {
                  setEditing(true);
                  requestAnimationFrame(() => inputRef.current?.focus());
                }}
                style={{
                  cursor: 'text',
                  color: 'var(--gray-12)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 480,
                }}
              >
                {state.config.name}
              </Text>
            </Tooltip>
          )}
          <Badge
            color={STATUS_COLOR[state.status]}
            variant="soft"
            size="2"
            data-testid="campaign-status"
            data-status={state.status}
          >
            {STATUS_LABEL[state.status]}
          </Badge>
        </Flex>

        {/* Middle: counters */}
        <Flex align="center" gap="4" wrap="wrap">
          <CounterBlock
            label="Runs"
            value={`${done} / ${state.config.budget}`}
            hint={`${totalPoints} total points`}
          />
          <CounterBlock label="In flight" value={`${inFlight}`} />
          <CounterBlock label="Autonomous" value={`${autonomous}`} />
          <CounterBlock label="Manual" value={`${manual}`} />
          <CounterBlock
            label="Cost"
            value={`${cumulativeFc.toFixed(1)} / ${maxCostFc.toFixed(1)} FC`}
            hint={`${state.config.flexCreditsPerHighFiRun} FC per high-fi run`}
          />
        </Flex>

        {/* Right: controls */}
        <Flex align="center" gap="2">
          {canPause && (
            <Button
              size="2"
              variant="soft"
              color="amber"
              disabled={controlMutation.isPending}
              onClick={() => sendControl('pause')}
            >
              Pause
            </Button>
          )}
          {canResume && (
            <Button
              size="2"
              variant="soft"
              color="green"
              disabled={controlMutation.isPending}
              onClick={() => sendControl('resume')}
            >
              Resume
            </Button>
          )}
          {canStop && (
            <AlertDialog.Root open={confirmStop} onOpenChange={setConfirmStop}>
              <AlertDialog.Trigger>
                <Button size="2" variant="soft" color="red">
                  Stop
                </Button>
              </AlertDialog.Trigger>
              <AlertDialog.Content maxWidth="440px">
                <AlertDialog.Title>Stop campaign?</AlertDialog.Title>
                <AlertDialog.Description size="2">
                  This ends the campaign immediately. Points already running
                  finish, but no new batches are proposed.
                </AlertDialog.Description>
                <Flex gap="2" justify="end" mt="4">
                  <AlertDialog.Cancel>
                    <Button variant="soft" color="gray">
                      Cancel
                    </Button>
                  </AlertDialog.Cancel>
                  <AlertDialog.Action>
                    <Button
                      color="red"
                      onClick={() => {
                        sendControl('stop');
                        setConfirmStop(false);
                      }}
                    >
                      Stop
                    </Button>
                  </AlertDialog.Action>
                </Flex>
              </AlertDialog.Content>
            </AlertDialog.Root>
          )}

          <Button size="2" variant="soft" onClick={() => setForkOpen(true)}>
            Fork
          </Button>
          <ForkModal
            campaignId={state.id}
            currentIteration={state.iteration}
            open={forkOpen}
            onClose={() => setForkOpen(false)}
          />
        </Flex>
      </Flex>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function CounterBlock({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  const content = (
    <Flex direction="column" align="start" style={{ lineHeight: 1.1 }}>
      <Text size="1" color="gray">
        {label}
      </Text>
      <Text size="2" weight="medium" style={{ color: 'var(--gray-12)' }}>
        {value}
      </Text>
    </Flex>
  );
  return hint ? <Tooltip content={hint}>{content}</Tooltip> : content;
}

export default CampaignHeader;
